// ============================================
// MEDFLOW - API Client
// ============================================

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

class ApiClient {
  private getToken(): string | null {
    return import.meta.env.VITE_ADMIN_API_KEY || null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'An error occurred');
    }

    return data;
  }

  // GET request
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Binary request (for audio/file responses)
  async requestBlob(endpoint: string, body: unknown): Promise<Blob> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error((data as { error?: string })?.error || 'Request failed');
    }
    return response.blob();
  }
}

export const api = new ApiClient();

// ============ AUTH API ============

export const authApi = {
  login: async (email: string, password: string) => {
    return api.post<{
      user: { id: string; email: string; name: string; phone?: string; role: string };
      clinic: { id: string; name: string; slug: string };
      token: string;
    }>('/auth/login', { email, password });
  },
  
  register: async (clinic: { name: string; slug: string }, user: { email: string; password: string; name: string }) => {
    return api.post<{
      user: { id: string; email: string; name: string; role: string };
      clinic: { id: string; name: string; slug: string };
      token: string;
    }>('/auth/register', { clinic, user });
  },
  
  me: async () => {
    return api.get('/auth/me');
  },
};

// ============ PATIENTS API ============

export const patientsApi = {
  list: async (params?: { phone?: string; name?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.phone) searchParams.append('phone', params.phone);
    if (params?.name) searchParams.append('name', params.name);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    
    const query = searchParams.toString();
    return api.get(`/patients${query ? `?${query}` : ''}`);
  },
  
  get: async (id: string) => {
    return api.get(`/patients/${id}`);
  },
  
  create: async (data: { name: string; phone: string; email?: string; birthDate?: string; notes?: string }) => {
    return api.post('/patients', data);
  },
  
  update: async (id: string, data: Partial<{ name: string; phone: string; email?: string; birthDate?: string; notes?: string }>) => {
    return api.put(`/patients/${id}`, data);
  },
  
  delete: async (id: string) => {
    return api.delete(`/patients/${id}`);
  },

  unblock: async (id: string) => {
    return api.put(`/patients/${id}/unblock`);
  },
};

// ============ APPOINTMENTS API ============

export const appointmentsApi = {
  list: async (params?: { 
    patientId?: string; 
    professionalId?: string; 
    startDate?: string; 
    endDate?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.patientId) searchParams.append('patientId', params.patientId);
    if (params?.professionalId) searchParams.append('professionalId', params.professionalId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    
    const query = searchParams.toString();
    return api.get(`/appointments${query ? `?${query}` : ''}`);
  },
  
  get: async (id: string) => {
    return api.get(`/appointments/${id}`);
  },
  
  create: async (data: {
    patientId: string;
    professionalId: string;
    serviceId: string;
    startTime: string;
    endTime: string;
    notes?: string;
  }) => {
    return api.post('/appointments', data);
  },
  
  update: async (id: string, data: Partial<{
    startTime?: string;
    endTime?: string;
    status?: string;
    notes?: string;
  }>) => {
    return api.put(`/appointments/${id}`, data);
  },
  
  cancel: async (id: string) => {
    return api.delete(`/appointments/${id}`);
  },

  lastChange: async (params: { since: string; startDate: string; endDate: string; professionalId?: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('since', params.since);
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    if (params.professionalId) searchParams.append('professionalId', params.professionalId);
    return api.get<{ hasChanges: boolean; lastChange: string; changedCount: number }>(
      `/appointments/last-change?${searchParams.toString()}`
    );
  },
};

// ============ PROFESSIONALS API ============

export const professionalsApi = {
  listAgents: async () => {
    return api.get<Array<{
      id: string;
      clinicId: string;
      professionalId: string;
      name: string;
      channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
      isActive: boolean;
      instructions: string;
      communicationTone?: string;
      workingHoursNotes?: string;
      treatmentNotes?: string;
      allowedServiceIds: string[];
      faqItems: string[];
      lineIdentifier?: string;
      n8nWorkflowId?: string;
      n8nWebhookUrl?: string;
      createdAt: string;
      updatedAt: string;
    }>>('/professionals/agents');
  },

  list: async (active?: boolean) => {
    return api.get(`/professionals${active !== undefined ? `?active=${active}` : ''}`);
  },
  
  get: async (id: string) => {
    return api.get(`/professionals/${id}`);
  },
  
  getSchedule: async (id: string) => {
    return api.get(`/professionals/${id}/schedule`);
  },

  getAgent: async (id: string) => {
    return api.get<{
      id: string;
      clinicId: string;
      professionalId: string;
      name: string;
      channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
      isActive: boolean;
      instructions: string;
      communicationTone?: string;
      workingHoursNotes?: string;
      treatmentNotes?: string;
      allowedServiceIds: string[];
      faqItems: string[];
      lineIdentifier?: string;
      createdAt: string;
      updatedAt: string;
    } | null>(`/professionals/${id}/agent`);
  },

  upsertAgent: async (
    id: string,
    data: {
      name: string;
      channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
      isActive: boolean;
      communicationTone?: string;
      faqItems: string[];
      lineIdentifier?: string;
    }
  ) => {
    return api.put(`/professionals/${id}/agent`, data);
  },

  syncN8nWorkflow: async (id: string) => {
    return api.post<{ action: 'created' | 'updated'; workflowId: string; workflowName: string; webhookUrl: string }>(
      `/professionals/${id}/sync-n8n-workflow`
    );
  },

  provision: async (data: { fullName: string; email: string; specialty: string; color?: string; phone?: string }) => {
    return api.post<{
      professional: { id: string; color: string; specialty: string; user: { name: string; email: string } } | null;
      temporaryPassword: string | null;
      createdNewUser: boolean;
    }>('/professionals/provision', data);
  },

  update: async (id: string, data: { specialty?: string; color?: string; isActive?: boolean; instagram?: string | null; linkedin?: string | null }) => {
    return api.put(`/professionals/${id}`, data);
  },

  activateAgent: async (id: string, data: { tone: 'amigable' | 'formal' | 'neutral'; specialNotes?: string }) => {
    return api.post<{
      agent: { id: string; name: string; channel: string; isActive: boolean } | null;
      webhookUrl: string | null;
      n8nAction: string | null;
    }>(`/professionals/${id}/activate-agent`, data);
  },
};

// ============ CHAT API ============

export const chatApi = {
  test: async (professionalId: string, message: string, sessionId: string) => {
    return api.post<{ reply: string | null; sessionId: string }>('/chat/test', {
      professionalId,
      message,
      sessionId,
    });
  },
  sessions: async (professionalId?: string) => {
    const qs = professionalId ? `?professionalId=${professionalId}` : '';
    return api.get(`/chat/sessions${qs}`);
  },
};

// ============ SERVICES API ============

export const servicesApi = {
  list: async (active?: boolean) => {
    return api.get(`/services${active !== undefined ? `?active=${active}` : ''}`);
  },
  
  get: async (id: string) => {
    return api.get(`/services/${id}`);
  },
  
  create: async (data: { name: string; description?: string; durationMinutes: number; price?: number }) => {
    return api.post('/services', data);
  },
  
  update: async (id: string, data: Partial<{ name: string; description?: string; durationMinutes: number; price?: number }>) => {
    return api.put(`/services/${id}`, data);
  },

  delete: async (id: string) => {
    return api.delete(`/services/${id}`);
  },
};

// ============ AVAILABILITY API ============

export const availabilityApi = {
  get: async (params: { date: string; professionalId?: string; serviceId?: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('date', params.date);
    if (params.professionalId) searchParams.append('professionalId', params.professionalId);
    if (params.serviceId) searchParams.append('serviceId', params.serviceId);
    
    return api.get(`/availability?${searchParams.toString()}`);
  },
};

// ============ BOXES API ============

export const boxesApi = {
  list: async (active?: boolean) => {
    return api.get<Array<{
      id: string;
      name: string;
      description?: string;
      isActive: boolean;
      schedules: Array<{
        id: string;
        dayOfWeek: number;
        startTime: string | null;
        endTime: string | null;
        startTime2: string | null;
        endTime2: string | null;
        isOpen: boolean;
      }>;
    }>>(`/boxes${active !== undefined ? `?active=${active}` : ''}`);
  },

  get: async (id: string) => {
    return api.get(`/boxes/${id}`);
  },

  create: async (data: { name: string; description?: string }) => {
    return api.post('/boxes', data);
  },

  update: async (id: string, data: { name?: string; description?: string; isActive?: boolean }) => {
    return api.put(`/boxes/${id}`, data);
  },

  updateSchedule: async (id: string, schedule: Array<{
    dayOfWeek: number;
    startTime?: string;
    endTime?: string;
    startTime2?: string;
    endTime2?: string;
    isOpen: boolean;
  }>) => {
    return api.put(`/boxes/${id}/schedule`, { schedule });
  },

  delete: async (id: string) => {
    return api.delete(`/boxes/${id}`);
  },
};

// ============ API KEYS API ============

export const apiKeysApi = {
  list: async () => {
    return api.get('/api-keys');
  },

  create: async (name: string) => {
    return api.post<{ id: string; name: string; key: string }>('/api-keys', { name });
  },

  revoke: async (id: string) => {
    return api.delete(`/api-keys/${id}`);
  },
};

// ============ FINANCE API ============

export interface FinanceQueryParams {
  startDate?: string;
  endDate?: string;
  professionalId?: string;
  serviceId?: string;
  boxId?: string;
  paymentMethod?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export const financeApi = {
  // Payments CRUD
  listPayments: async (params?: FinanceQueryParams) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.professionalId) searchParams.append('professionalId', params.professionalId);
    if (params?.serviceId) searchParams.append('serviceId', params.serviceId);
    if (params?.boxId) searchParams.append('boxId', params.boxId);
    if (params?.paymentMethod) searchParams.append('paymentMethod', params.paymentMethod);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));

    const query = searchParams.toString();
    return api.get(`/finance/payments${query ? `?${query}` : ''}`);
  },

  createPayment: async (data: {
    patientId: string;
    appointmentId?: string;
    professionalId?: string;
    serviceId?: string;
    boxId?: string;
    amount: number;
    paymentMethod: string;
    status: string;
    description?: string;
    paidAt: string;
  }) => {
    return api.post('/finance/payments', data);
  },

  updatePayment: async (id: string, data: {
    amount?: number;
    paymentMethod?: string;
    status?: string;
    description?: string;
  }) => {
    return api.put(`/finance/payments/${id}`, data);
  },

  deletePayment: async (id: string) => {
    return api.delete(`/finance/payments/${id}`);
  },

  // Analytics endpoints
  summary: async (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get(`/finance/summary${query ? `?${query}` : ''}`);
  },

  byService: async (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get(`/finance/by-service${query ? `?${query}` : ''}`);
  },

  byProfessional: async (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get(`/finance/by-professional${query ? `?${query}` : ''}`);
  },

  byBox: async (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get(`/finance/by-box${query ? `?${query}` : ''}`);
  },

  daily: async (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return api.get(`/finance/daily${query ? `?${query}` : ''}`);
  },
};

// ============ VOICE API ============

export const voiceApi = {
  synthesize: async (text: string, voice?: string, instructions?: string): Promise<Blob> => {
    return api.requestBlob('/voice/synthesize', { text, voice, instructions });
  },
};
